import { type FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatter, uuidToColor } from '@repo/shared';
import { useRouter } from '@tanstack/react-router';

import type { AggregateClusterResponse } from '@/api/generated.schemas';
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import ClusterMetricsDetails from '@/routes/(app)/(clusters)/components/cluster-metrics-details';

type ClusterMetric = AggregateClusterResponse[number];

interface ClusterMetricsProps {
  cluster: ClusterMetric;
  active?: boolean;
  isEval: boolean;
}

const ClusterMetrics: FC<ClusterMetricsProps> = ({
  cluster,
  active,
  isEval,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const handleNavigate = useCallback(async () => {
    await router.navigate({
      to: '/app/clusters/$clusterId',
      params: {
        clusterId: cluster.id,
      },
    });
  }, [cluster.id, router]);

  return (
    <Card
      className={cn('w-[350px]', active && 'shadow-2xl')}
      style={{ borderLeft: `4px solid ${uuidToColor(cluster.id)}` }}
      onClick={handleNavigate}>
      <CardHeader>
        <CardTitle>
          {cluster.dataset.name} - {cluster.model}
        </CardTitle>
        <CardDescription>
          {t('comparison.createdAt', {
            data: formatter.date.dateTime(
              new Date(cluster.createdAt as string)
            ),
          })}
        </CardDescription>
        <CardDescription>
          {t('comparison.details', {
            cost: formatter.metrics.cost(cluster.metrics?.cost),
            latency: formatter.metrics.time(cluster.metrics?.time),
          })}
        </CardDescription>
      </CardHeader>
      {isEval && <ClusterMetricsDetails cluster={cluster} />}
    </Card>
  );
};

export default ClusterMetrics;
